import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useGetCreateClientDeliverableById } from "../../../../../../api/services/client/clientApis";
import { InformationBadge } from "../../../../../../common/informationBadge/informationBadge";
import {
  MuiDoubleLabel,
  MuiGrid,
  MuiLink,
  MuiModal,
} from "../../../../../../components";
import { replaceHashWithValue } from "../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { CancelFieldModal } from "../cancelFieldModal/CancelFieldModal";
import { ViewDeliverablesWrapper } from "./ViewDeliverables.style";
import { ViewManageLocale } from "../../../../../../components/manageLocale/ViewManageLocale";
import { UnitTypesKey } from "../../../../../../helpers/constants/siteCoreKeys";

interface ViewDeliverablesProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  selectedDeliverableId: string;
  isView?: boolean;
  editButtonClick?: () => void;
}

export const ViewDeliverables: FC<ViewDeliverablesProps> = ({
  show,
  handleCloseModal,
  pageId,
  selectedDeliverableId,
  isView,
  editButtonClick,
}) => {
  const dispatch = useDispatch();
  const gridSize = { md: 4, sm: 6, xs: 12 };

  const [showManageLocale, setShowManageLocale] = useState(false);

  const handleShowManageLocale = () => {
    setShowManageLocale(true);
  };

  const handleCloseShowManageLocale = () => {
    setShowManageLocale(false);
  };

  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();

  const [showCancel, setShowCancel] = useState(false);

  const {
    data: deliverableData,
    isFetching,
    isError: isDeliverableDataError,
    refetchWithParams: getDeliverableData,
  } = useGetCreateClientDeliverableById();

  useEffect(() => {
    getDeliverableData(selectedDeliverableId);
  }, []);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isFetching]);

  useEffect(() => {
    if (isDeliverableDataError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isDeliverableDataError]);

  const getLabel = (key: string) => {
    return fetchLabel(
      activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
      key,
    );
  };

  const getUnitText = () => {
    if (deliverableData?.units === UnitTypesKey.Minutes) {
      return activitiesAndDeliverablesSiteCoreKeys.createDeliverableMinutes;
    } else if (deliverableData?.units === UnitTypesKey.Count) {
      return activitiesAndDeliverablesSiteCoreKeys.createDeliverableQuantity;
    } else if (deliverableData?.units === UnitTypesKey.Days) {
      return activitiesAndDeliverablesSiteCoreKeys.createDeliverableNumberOfDays;
    } else {
      return activitiesAndDeliverablesSiteCoreKeys.halfDay;
    }
  };

  const getMeasurementValue = () => {
    if (!deliverableData?.units) {
      return "";
    }
    if (deliverableData.units === UnitTypesKey.Minutes) {
      return fetchValueByKeyOfDropdown(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableMinuteList,
        deliverableData.measurements,
      );
    }
    return deliverableData.measurements;
  };

  const unitValue = fetchValueByKeyOfDropdown(
    activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
    activitiesAndDeliverablesSiteCoreKeys.createDeliverableUnitList,
    deliverableData?.units ?? "",
  );

  const handleSecondayButtonClick = () => {
    handleCloseModal();
  };

  const bannerInfoMessage = fetchLabel(
    activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
    activitiesAndDeliverablesSiteCoreKeys.editDeliverableNote,
  );

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    handleCloseModal();
  };

  const title = replaceHashWithValue(
    getLabel(activitiesAndDeliverablesSiteCoreKeys.viewDeliverable),
    deliverableData?.name ?? "",
  );

  const closeModalHandler = () => setShowCancel(false);
  return (
    <>
      <MuiModal
        open={show}
        onClose={handleCloseModal}
        title={title}
        secondaryButtonTitle={commonLabels.close}
        onSecondaryButtonClick={handleSecondayButtonClick}
        tertiaryButtonTitle={
          !isView
            ? getLabel(
                activitiesAndDeliverablesSiteCoreKeys.editDeliverableButtonText,
              )
            : ""
        }
        onTertiaryButtonClick={editButtonClick}
        hasLargeContent
        stackButtonsOnMobile={true}
      >
        <>
          <InformationBadge message={bannerInfoMessage} />
          <ViewDeliverablesWrapper>
            <MuiGrid container data-testid={`${pageId}-view-deliverables`}>
              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  label={getLabel(
                    activitiesAndDeliverablesSiteCoreKeys.deliverableViewType,
                  )}
                  value={fetchValueByKeyOfDropdown(
                    activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                    activitiesAndDeliverablesSiteCoreKeys.deliverableTypeList,
                    deliverableData?.deliverableType ?? "",
                  )}
                />
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  label={getLabel(
                    activitiesAndDeliverablesSiteCoreKeys.deliverablesViewName,
                  )}
                  value={deliverableData?.name ?? ""}
                  isValueWithEllipsis
                  valueClasses="deliverable-value"
                />
                <MuiLink
                  className="view-locale-link"
                  onClick={handleShowManageLocale}
                >
                  {commonLabels.locale}
                </MuiLink>
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  label={getLabel(
                    activitiesAndDeliverablesSiteCoreKeys.deliverablesUnits,
                  )}
                  value={unitValue}
                />
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <MuiDoubleLabel
                  label={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                    getUnitText(),
                  )}
                  value={getMeasurementValue()}
                />
              </MuiGrid>
            </MuiGrid>

            {showCancel && (
              <CancelFieldModal
                showModal={showCancel}
                handleModalClose={closeModalHandler}
                handlePrimaryButtonClick={cancelConfirmHandler}
              />
            )}
          </ViewDeliverablesWrapper>
        </>
      </MuiModal>
      {showManageLocale && (
        <ViewManageLocale
          show={showManageLocale}
          handleCloseModal={handleCloseShowManageLocale}
          localeLanguages={deliverableData?.localizedDeliverableNames ?? []}
          title={title}
          bannerInfoMessage={bannerInfoMessage}
        />
      )}
    </>
  );
};
